import { type FC, type ReactNode, useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "./sidebar";
import { navEntries, isSeparator } from "./sidebar_nav";
import type { User } from "../stores/user_store";

interface LayoutProps {
  user: User;
  children: ReactNode;
}

export const Layout: FC<LayoutProps> = ({ user, children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Task 8.2: drawer state and event handlers
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Close drawer on Escape key
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [drawerOpen]);

  return (
    <div className="vmx-layout-root">
      {/* Task 8.3: Mobile header with hamburger button and logo */}
      <header className="vmx-mobile-header">
        <button aria-label="Open menu" onClick={() => setDrawerOpen(true)}>
          ☰
        </button>
        <img src="/favicon.svg" alt="SecureCycle" width={28} height={27} />
        <span>SecureCycle</span>
      </header>

      {/* Task 8.4: Mobile drawer and backdrop */}
      {drawerOpen && (
        <>
          <div
            className="vmx-mobile-drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
          />
          <nav className="vmx-mobile-drawer">
            {/* Logo / Brand */}
            <div
              style={{
                height: "var(--vmx-header-height)",
                display: "flex",
                alignItems: "center",
                padding: "0 24px",
                gap: 12,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <img
                src="/favicon.svg"
                alt="SecureCycle"
                width={36}
                height={35}
              />
              <div>
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#fff",
                    lineHeight: 1.2,
                    letterSpacing: "-0.3px",
                  }}
                >
                  SecureCycle
                </div>
                <div
                  style={{
                    fontSize: "0.6875rem",
                    color: "var(--vmx-sidebar-text)",
                    opacity: 0.6,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Verimatrix
                </div>
              </div>
            </div>

            {/* Navigation links */}
            <nav
              style={{
                flex: 1,
                padding: "16px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {navEntries.map((entry, idx) => {
                if (isSeparator(entry)) {
                  return (
                    <div
                      key={`sep-${idx}`}
                      style={{
                        height: 1,
                        background: "rgba(255,255,255,0.08)",
                        margin: "8px 16px",
                      }}
                    />
                  );
                }
                const item = entry;
                const isActive =
                  item.path === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.path);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setDrawerOpen(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 16px",
                      borderRadius: "var(--vmx-radius)",
                      fontSize: "0.875rem",
                      fontWeight: isActive ? 600 : 500,
                      color: isActive
                        ? "var(--vmx-sidebar-active-text)"
                        : "var(--vmx-sidebar-text)",
                      background: isActive
                        ? "var(--vmx-sidebar-active)"
                        : "transparent",
                      textDecoration: "none",
                      transition: "all var(--vmx-transition)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "1.125rem",
                        width: 24,
                        textAlign: "center",
                      }}
                    >
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* User profile and sign-out */}
            <div
              style={{
                padding: "12px",
                borderTop: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, var(--vmx-accent-dark), var(--vmx-accent))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    flexShrink: 0,
                  }}
                >
                  {user.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "rgba(255,255,255,0.9)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.name}
                  </div>
                  <div
                    style={{
                      fontSize: "0.6875rem",
                      color: "rgba(255,255,255,0.4)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.email}
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: "0.625rem",
                  color: "rgba(255,255,255,0.3)",
                  textAlign: "center",
                  padding: "6px 0 2px",
                  letterSpacing: "0.5px",
                }}
              >
                {__BUILD_VERSION__}
              </div>
              <button
                className="vmx-btn vmx-btn-ghost"
                style={{
                  width: "100%",
                  justifyContent: "flex-start",
                  color: "var(--vmx-danger)",
                  borderRadius: "var(--vmx-radius-sm)",
                  marginTop: 4,
                }}
                onClick={() => {
                  setDrawerOpen(false);
                  navigate("/logout");
                }}
              >
                ↪ Sign out
              </button>
            </div>
          </nav>
        </>
      )}

      <Sidebar user={user} />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <main style={{ flex: 1, overflow: "auto" }}>{children}</main>
      </div>
    </div>
  );
};
